import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import { useAuth } from '../hooks/useAuth';

export interface Soundworld {
    id: string;
    name: string;
    name_pretty: string;
    author: string;
    subtitle: string;
    description: string;
    image: string;
    status?: string;
    elements?: Record<string, {
        name_pretty: string;
        [key: string]: any;
    }>;
    sample?: string;
}

interface SoundworldsContextType {
    soundworlds: Soundworld[];
    isLoading: boolean;
    error: string | null;
    hasLoaded: boolean;
}

const SoundworldsContext = createContext<SoundworldsContextType | undefined>(undefined);

export const useSoundworldsContext = () => {
    const context = useContext(SoundworldsContext);
    if (!context) {
        throw new Error('useSoundworldsContext must be used within a SoundworldsProvider');
    }
    return context;
};

interface SoundworldsProviderProps {
    children: React.ReactNode;
}

export const SoundworldsProvider: React.FC<SoundworldsProviderProps> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [soundworlds, setSoundworlds] = useState<Soundworld[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const loadingRef = useRef(false);

    const loadSoundworlds = useCallback(async () => {

        if (hasLoaded || loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LIST_SOUNDWORLDS}`, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const transformedSoundworlds: Soundworld[] = Object.entries(data.soundworlds || {}).map(([id, soundworld]: [string, any]) => {
                return {
                    id,
                    name: id,
                    name_pretty: soundworld.name_pretty || id,
                    author: soundworld.author || 'Unknown',
                    subtitle: soundworld.subtitle || '',
                    description: soundworld.description || '',
                    image: soundworld.image || '',
                    status: 'active',
                    elements: soundworld.elements || {},
                    sample: soundworld.preview || undefined
                };
            });

            setSoundworlds(transformedSoundworlds);
            setHasLoaded(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch soundworlds');
            setSoundworlds([]);
            setHasLoaded(true);
        } finally {
            setIsLoading(false);
            loadingRef.current = false;
        }
    }, []);

    useEffect(() => {

        if (isAuthenticated && !hasLoaded && !loadingRef.current) {
            loadSoundworlds();
        }

        if (!isAuthenticated) {
            setSoundworlds([]);
            setHasLoaded(false);
            setError(null);
            loadingRef.current = false;
        }
    }, [loadSoundworlds, hasLoaded, isAuthenticated]);

    const value = {
        soundworlds,
        isLoading,
        error,
        hasLoaded,
    };

    return (
        <SoundworldsContext.Provider value={ value }>
            { children }
        </SoundworldsContext.Provider>
    );
}; 